import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';
import { buildDelimitedText } from '../../../utils/exportTable';
import {
  buildDataMapTable,
  buildVarInfos,
  type DataExportVarInfo,
} from '../../../utils/dataExportVars';
import { canExportText, copyTextToClipboard, downloadTextFile } from '../../../web/textExport';
import { OrderedDualListSelector, type OrderedOption } from '../account/OrderedDualListSelector';

interface DataExportSetupViewProps {
  storage?: any;
  assessmentId: string;
  /** Selected variable ids (the full MVar), in the order they are sent as DataExportRequestDto.vars. */
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  onBack: () => void;
}

/** Twice the selector's default of 6 visible rows. */
const LISTBOX_ROWS = 12;

export const DataExportSetupView: React.FC<DataExportSetupViewProps> = ({
  storage = null,
  assessmentId,
  selectedIds,
  onChange,
  onBack,
}) => {
  const [assessmentLabel, setAssessmentLabel] = useState(assessmentId);
  const [vars, setVars] = useState<DataExportVarInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // The variables are the assessment's aggVars — bundled local content, no network fetch.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        if (!storage) {
          throw new Error('Assessment content unavailable.');
        }
        const assessments = await new AdminAssessmentStorage(storage).getAvailableAssessments();
        const assessment = assessments.find((a) => a.sourceId === assessmentId);
        if (!cancelled) {
          setAssessmentLabel(assessment?.dName || assessmentId);
          setVars(buildVarInfos(assessment?.aggVars ?? []));
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : String(loadError));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assessmentId, storage]);

  const options = useMemo<OrderedOption[]>(() => vars.map((v) => ({ id: v.id, label: v.label })), [vars]);
  const flashMessage = (text: string) => {
    setMessage(text);
    setTimeout(() => setMessage((current) => (current === text ? null : current)), 3000);
  };

  // With nothing selected the data map covers every available variable.
  const dataMap = useMemo(
    () => buildDataMapTable(selectedIds.length > 0 ? selectedIds : vars.map((v) => v.id), vars),
    [selectedIds, vars]
  );
  const canExport = canExportText();

  const handleExportMap = () => {
    const safeName = assessmentLabel.replace(/[^A-Za-z0-9_-]+/g, '_');
    const ok = downloadTextFile(`${safeName}_data_map.csv`, buildDelimitedText(dataMap, ','));
    flashMessage(ok ? `Exported ${dataMap.rows.length} variables` : 'Export is only available in the web app');
  };

  const handleCopyMap = async () => {
    const ok = await copyTextToClipboard(buildDelimitedText(dataMap, '\t', '\n'));
    flashMessage(ok ? `Copied data map for ${dataMap.rows.length} variables` : 'Copy failed');
  };

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.panel}>
      <View style={styles.headerRow}>
        <Pressable onPress={onBack} accessibilityRole="button" accessibilityLabel="Back">
          <Text style={styles.backLink}>← Back</Text>
        </Pressable>
      </View>

      <Text style={styles.title}>Data Export Setup — {assessmentLabel}</Text>
      <Text style={styles.helpText}>
        Choose the variables to include as columns. With none selected the export returns all variables by default.
      </Text>

      {error ? (
        <View style={styles.errorCard}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      {isLoading ? (
        <Text style={styles.helpText}>Loading variables…</Text>
      ) : options.length === 0 ? (
        <Text style={styles.helpText}>No variables found for this assessment.</Text>
      ) : (
        <View style={styles.selectorWrap}>
          <OrderedDualListSelector
            options={options}
            selectedIds={selectedIds}
            onChange={onChange}
            availableTitle="Available Variables"
            selectedTitle="Selected Variables"
            listboxRows={LISTBOX_ROWS}
          />
        </View>
      )}

      {canExport ? (
        <View style={styles.buttonRow}>
          <Pressable
            style={styles.actionButton}
            onPress={handleExportMap}
            accessibilityRole="button"
            accessibilityLabel="Export Data Map"
          >
            <Text style={styles.actionButtonText}>Export Data Map</Text>
          </Pressable>
          <View style={styles.buttonSpacer} />
          <Pressable
            style={styles.actionButton}
            onPress={() => void handleCopyMap()}
            accessibilityRole="button"
            accessibilityLabel="Copy Data Map"
          >
            <Text style={styles.actionButtonText}>Copy All</Text>
          </Pressable>
          {message ? <Text style={styles.messageText}>{message}</Text> : null}
        </View>
      ) : null}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
  },
  panel: {
    padding: 20,
    maxWidth: 900,
    width: '100%',
    alignSelf: 'center',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  backLink: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'none',
  },
  title: {
    ...editorText,
    fontSize: 16,
    fontWeight: '600',
    color: EDITOR_COLORS.textPrimary,
    marginBottom: 6,
  },
  helpText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    marginBottom: 12,
  },
  selectorWrap: {
    marginBottom: 16,
  },
  buttonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  buttonSpacer: {
    width: 12,
  },
  actionButton: {
    height: 28,
    paddingHorizontal: 16,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  messageText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
    marginLeft: 16,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 20,
  },
});
